// src/rms/components/LocalePicker.jsx
// ============================================================================
// Language picker
// ============================================================================
// Every language is listed in its OWN script and spelled the way its speakers
// spell it -- "Español", not "Spanish"; "日本語", not "Japanese". A picker that
// lists languages in English is only usable by someone who already reads
// English, which is exactly the person who does not need it.
//
// Deliberately independent of RmsContext. The landing page, the sign-in screen
// and the vertical picker all render outside RmsProvider, and a visitor who
// cannot read the site is precisely the person who needs this control -- before
// they have an account, let alone an operator.
//
// A plain <select> on purpose. It is the one control every platform renders in
// the user's own language, keyboard-navigable and screen-reader-labelled for
// free, and it does not trap focus on a phone.
// ============================================================================

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LOCALES,
  getLocale,
  chooseLocale,
  subscribeToLocale,
} from "../i18n/locale";

function LocalePicker({ className = "", compact = false }) {
  const { t } = useTranslation();
  const [locale, setLocaleState] = useState(getLocale);

  // The active locale lives in module state, so more than one picker can be
  // mounted (header and settings, say) and both stay in step.
  useEffect(() => subscribeToLocale(setLocaleState), []);

  const label = t("locale.change", "Change language");

  return (
    <label className={`rms-locale-picker ${className}`.trim()}>
      <span className="rms-sr-only">{label}</span>
      <select
        className="rms-select rms-select--sm"
        value={locale}
        onChange={(e) => chooseLocale(e.target.value)}
        aria-label={label}
      >
        {SUPPORTED_LOCALES.map((l) => (
          // lang= on each option so a screen reader pronounces it with the
          // right voice rather than reading Japanese in an English one.
          <option key={l.code} value={l.code} lang={l.code}>
            {compact ? l.code.toUpperCase() : l.endonym}
          </option>
        ))}
      </select>
    </label>
  );
}

export default LocalePicker;
